import type { LiveResultsPaging } from '@models/api';

/** B3b: more than 6 questions shown → pages of 6 (two columns of 3), RESOLUTIONS L22. */
export const RESULTS_PAGE_SIZE = 6;
/** Results pages rotate every 8 seconds (TECH-867 § Decided). */
export const RESULTS_PAGE_MS = 8_000;

export const resultsPageCount = (shown: number): number =>
  Math.max(1, Math.ceil(shown / RESULTS_PAGE_SIZE));

export interface ShownResultsPage {
  /** 0-based. */
  page: number;
  pages: number;
  paused: boolean;
  /** Whole seconds until the next page (1–8); null while paused or with one page. */
  secondsLeft: number | null;
}

/**
 * The page the screen shows, computed from the server-held paging (CONTRACT "Plan review changes" PR2):
 * `paused ? page : (page + floor((now − page_since) / 8 s)) mod pages`. So a pause or skip from the host view
 * moves every screen at its next read. With no paging block (one page, or an old state) it rotates from
 * `fallbackSince` (the End time), as the server rebuilds it on a Redis miss. A clock behind the server's
 * counts as no time elapsed rather than going backwards.
 */
export const shownResultsPage = (
  paging: LiveResultsPaging | null | undefined,
  shown: number,
  nowMs: number,
  fallbackSince: string | null,
): ShownResultsPage => {
  const pages = resultsPageCount(shown);
  if (pages <= 1) return { page: 0, pages, paused: false, secondsLeft: null };

  const base = paging ?? {
    page: 0,
    paused: false,
    page_since: fallbackSince ?? new Date(nowMs).toISOString(),
  };
  const start = ((Math.trunc(base.page) % pages) + pages) % pages;
  if (base.paused) return { page: start, pages, paused: true, secondsLeft: null };

  const since = Date.parse(base.page_since);
  const elapsed = Number.isFinite(since) ? Math.max(0, nowMs - since) : 0;
  const steps = Math.floor(elapsed / RESULTS_PAGE_MS);
  const intoPage = elapsed - steps * RESULTS_PAGE_MS;
  return {
    page: (start + steps) % pages,
    pages,
    paused: false,
    secondsLeft: Math.max(1, Math.ceil((RESULTS_PAGE_MS - intoPage) / 1000)),
  };
};
